'use client';

import { useEffect, useRef, useState } from 'react';
import { useShippingProgressBar } from 'pages/Cart/Sidebar/components/ShippingProgressBar/hooks';
import {
  gtmCurrentBasket,
  gtmPushViewCart,
} from 'modules/gtm/modules/sections/cart/cart';
import { CurrentBasket } from 'modules/gtm/modules/types';
import { parseDataToGTMCart } from 'modules/gtm/utils/parsers';
import { divide } from 'modules/math';
import { usePathname } from 'modules/next/hooks';
import { useCart } from 'modules/shop/cart/hooks';
import { CartItem } from 'commerce/scayle/user-interaction/types';
import { Product } from 'commerce/scayle/utils/adapter';

export const useGTMCartView = () => {
  const [isCartAvailable, setIsCartAvailable] = useState(false);
  const { cart } = useCart();
  const gtmCartData = parseDataToGTMCart(cart);

  useEffect(() => {
    isCartAvailable &&
      gtmCartData &&
      gtmPushViewCart(gtmCartData.items, gtmCartData.currency);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCartAvailable]);

  useEffect(() => {
    cart && setIsCartAvailable(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);
};

const parseCart = (cart: { items: CartItem[] }) => {
  return cart.items.reduce(
    (acc, cartItem) => {
      const { variant, price, quantity } = cartItem;
      const { name, referenceKey } = Product.createCartProduct(cartItem);
      const priceWithTax = divide(price.total.withTax as number, 100);

      return {
        quantity_total: acc.quantity_total + quantity,
        matsids: [...acc.matsids, variant.referenceKey],
        products: [
          ...acc.products,
          {
            name,
            product_id: referenceKey,
            price: priceWithTax,
            quantity,
          },
        ],
      } as CurrentBasket;
    },
    {
      value: 0,
      value_excl_shipping: 0,
      quantity_total: 0,
      matsids: [],
      products: [],
    } as CurrentBasket,
  );
};

export const useGTMCurrentBasket = () => {
  const { cart } = useCart();
  const { shippingPrice } = useShippingProgressBar();
  const pathname = usePathname();
  const lastPathname = useRef('');

  useEffect(() => {
    if (cart && lastPathname.current !== pathname) {
      const currentBasket = parseCart(cart);

      gtmCurrentBasket(
        cart.items.length
          ? {
              ...currentBasket,
              value_excl_shipping: cart.cost.withTax,
              value: cart.cost.withTax + shippingPrice,
            }
          : null,
      );

      lastPathname.current = pathname;
    }
  }, [pathname, cart, shippingPrice]);
};
